import React, { useState } from "react";
import UseAnimations from "react-useanimations";
import editIcon from "react-useanimations/lib/edit";
import trash2 from "react-useanimations/lib/trash2";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Checkbox } from "../../components/ui/checkbox";
import { toast } from "react-toastify";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "../../components/ui/popover";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "../../components/ui/table";
import { getLengths } from "../../services/frequencyLength";
import { getFrequencies } from "../../services/frequencyService";
import {
  addNewFrequency,
  deleteFrequency,
  updateFrequency,
  setFrequencySoftDeleted,
} from "../../services/frequencyService";
import {
  addNewLengths,
  deleteLength,
  updateLength,
  setLengthSoftDeleted,
} from "../../services/frequencyLength";
const PrescriptionFrequencyLength: React.FC = () => {
  const [deletePopover, setDeletePopover] = useState<{ [id: string]: boolean }>(
    {}
  );
  const [deleteInput, setDeleteInput] = useState<{ [id: string]: string }>({});
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [translationError, setTranslationError] = useState("");
  // Frequency: Name, Description, Translation
  type FrequencyType = {
    id: string;
    name: string;
    description: string;
    translation: string;
    soft_deleted?: boolean;
  };
  const [frequencies, setFrequencies] = useState<FrequencyType[]>([]);
  async function fetchFrequencies() {
    try {
      // include_deleted=true so the admin can see and un-hide soft-deleted rows.
      const data = await getFrequencies(true);
      setFrequencies(data || []);
      toast.success("Frequencies loaded successfully");
    } catch (error) {
      // Optionally handle error
    }
  }
  async function fetchLengths() {
    try {
      // include_deleted=true so the admin can see and un-hide soft-deleted rows.
      const data = await getLengths(true);
      setLengths(data || []);
      toast.success("Lengths loaded successfully");
    } catch (error) {
      // Optionally handle error
    }
  }
  React.useEffect(() => {
    fetchFrequencies();
  }, []);
  type LengthType = {
    id: string;
    name: string;
    description: string;
    soft_deleted?: boolean;
  };
  const [lengths, setLengths] = useState<LengthType[]>([]);
  const [lengthDeletePopover, setLengthDeletePopover] = useState<{
    [name: string]: boolean;
  }>({});
  const [lengthDeleteInput, setLengthDeleteInput] = useState<{
    [name: string]: string;
  }>({});
  const [lengthDeleteLoading, setLengthDeleteLoading] = useState(false);
  const [newFrequency, setNewFrequency] = useState<FrequencyType>({
    id: "",
    name: "",
    description: "",
    translation: "",
  });
  const [newLength, setNewLength] = useState<LengthType>({
    id: "",
    name: "",
    description: "",
  });
  React.useEffect(() => {
    fetchLengths();
  }, []);

  // Validation helpers
  const isFrequencyValid =
    newFrequency.name.trim() &&
    newFrequency.description.trim() &&
    newFrequency.translation.trim() &&
    /^[|\-]+$/.test(newFrequency.translation.trim()); // keep for context

  const isLengthValid = newLength.name.trim() && newLength.description.trim();

  async function addFrequency() {
    // Validate all fields are non-empty
    if (
      !newFrequency.name.trim() ||
      !newFrequency.description.trim() ||
      !newFrequency.translation.trim()
    ) {
      toast.error("Please fill all Frequency fields.");
      return;
    }
    // Validate translation field only contains '|' and '-'
    // '-' does not need to be escaped inside character class if placed at the end
    if (!/^[|\-]+$/.test(newFrequency.translation.trim())) {
      // keep for context
      toast.error("Translation field must only contain '|' and '-' characters.");
      return;
    }
    try {
      await addNewFrequency({
        frequencyName: newFrequency.name.trim(),
        frequencyDescription: newFrequency.description.trim(),
        frequencyTranslation: newFrequency.translation.trim(),
      });
      toast.success("Frequency added successfully");
      setNewFrequency({ id: "", name: "", description: "", translation: "" });
      fetchFrequencies();
    } catch (error: any) {
      // error.message carries the backend detail (e.g. 409 duplicate name)
      toast.error(error?.message || "Failed to add frequency.");
    }
  }

  async function addLength() {
    // Validate all fields are non-empty
    if (!newLength.name.trim() || !newLength.description.trim()) {
      alert("Please fill all Length fields.");
      return;
    }
    try {
      await addNewLengths({
        lengthName: newLength.name.trim(),
        flengthDescription: newLength.description.trim(),
      });
      toast.success("Length added successfully");
      setNewLength({ id: "", name: "", description: "" });
      fetchLengths();
    } catch (error) {
      alert("Failed to add length.");
    }
  }

  // ---- Edit: Frequency ----
  const [editFreqPopover, setEditFreqPopover] = useState<{
    [id: string]: boolean;
  }>({});
  const [editFreqLoading, setEditFreqLoading] = useState(false);
  const [editFreqTranslationError, setEditFreqTranslationError] = useState("");
  const [editFreq, setEditFreq] = useState<FrequencyType>({
    id: "",
    name: "",
    description: "",
    translation: "",
  });

  function startEditFrequency(freq: FrequencyType) {
    setEditFreq({ ...freq });
    setEditFreqTranslationError("");
    setEditFreqPopover((prev) => ({ ...prev, [freq.id]: true }));
  }

  async function saveFrequencyEdit() {
    if (
      !editFreq.name.trim() ||
      !editFreq.description.trim() ||
      !editFreq.translation.trim()
    ) {
      toast.error("Please fill all Frequency fields.");
      return;
    }
    if (!/^[|\-]+$/.test(editFreq.translation.trim())) {
      toast.error("Translation field must only contain '|' and '-' characters.");
      return;
    }
    setEditFreqLoading(true);
    try {
      await updateFrequency(editFreq.id, {
        frequencyName: editFreq.name.trim(),
        frequencyDescription: editFreq.description.trim(),
        frequencyTranslation: editFreq.translation.trim(),
      });
      toast.success("Frequency updated successfully");
      setEditFreqPopover((prev) => ({ ...prev, [editFreq.id]: false }));
      fetchFrequencies();
    } catch (err: any) {
      // err.message carries the backend detail (e.g. 409 duplicate name)
      toast.error(err?.message || "Failed to update frequency");
    } finally {
      setEditFreqLoading(false);
    }
  }

  // ---- Edit: Length ----
  const [editLengthPopover, setEditLengthPopover] = useState<{
    [id: string]: boolean;
  }>({});
  const [editLengthLoading, setEditLengthLoading] = useState(false);
  const [editLengthState, setEditLengthState] = useState<LengthType>({
    id: "",
    name: "",
    description: "",
  });

  function startEditLength(len: LengthType) {
    setEditLengthState({ ...len });
    setEditLengthPopover((prev) => ({ ...prev, [len.id]: true }));
  }

  async function saveLengthEdit() {
    if (!editLengthState.name.trim() || !editLengthState.description.trim()) {
      toast.error("Please fill all Length fields.");
      return;
    }
    setEditLengthLoading(true);
    try {
      await updateLength(editLengthState.id, {
        lengthName: editLengthState.name.trim(),
        flengthDescription: editLengthState.description.trim(),
      });
      toast.success("Length updated successfully");
      setEditLengthPopover((prev) => ({ ...prev, [editLengthState.id]: false }));
      fetchLengths();
    } catch (err: any) {
      // err.message carries the backend detail (e.g. 409 duplicate name)
      toast.error(err?.message || "Failed to update length");
    } finally {
      setEditLengthLoading(false);
    }
  }

  // ---- Soft delete toggles ----
  const [softDeleteLoading, setSoftDeleteLoading] = useState<{
    [id: string]: boolean;
  }>({});

  async function toggleFrequencySoftDeleted(
    freq: FrequencyType,
    checked: boolean
  ) {
    setSoftDeleteLoading((prev) => ({ ...prev, [freq.id]: true }));
    try {
      await setFrequencySoftDeleted(freq.id, checked);
      toast.success(
        checked
          ? `"${freq.name}" hidden from patient page`
          : `"${freq.name}" restored to patient page`
      );
      fetchFrequencies();
    } catch (err: any) {
      toast.error(err?.message || "Failed to update frequency");
    } finally {
      setSoftDeleteLoading((prev) => ({ ...prev, [freq.id]: false }));
    }
  }

  async function toggleLengthSoftDeleted(len: LengthType, checked: boolean) {
    setSoftDeleteLoading((prev) => ({ ...prev, [len.id]: true }));
    try {
      await setLengthSoftDeleted(len.id, checked);
      toast.success(
        checked
          ? `"${len.name}" hidden from patient page`
          : `"${len.name}" restored to patient page`
      );
      fetchLengths();
    } catch (err: any) {
      toast.error(err?.message || "Failed to update length");
    } finally {
      setSoftDeleteLoading((prev) => ({ ...prev, [len.id]: false }));
    }
  }

  return (
    <div className="max-w-2xl mx-auto p-4">
      <h2 className="text-lg font-semibold mb-4">
        Prescription Frequency & Length
      </h2>
      <div className="mb-6">
        <h3 className="font-medium mb-2">Frequencies</h3>
        <div className="flex gap-2 mb-2">
          <Input
            value={newFrequency.name}
            onChange={(e) =>
              setNewFrequency((f) => ({ ...f, name: e.target.value }))
            }
            placeholder="Name"
            className="w-32"
          />
          <Input
            value={newFrequency.description}
            onChange={(e) =>
              setNewFrequency((f) => ({ ...f, description: e.target.value }))
            }
            placeholder="Description"
            className="w-48"
          />
          <Input
            value={newFrequency.translation}
            onChange={(e) => {
              const value = e.target.value;
              if (/^[|\-]*$/.test(value)) {
                setNewFrequency((f) => ({ ...f, translation: value }));
                setTranslationError("");
              } else {
                setTranslationError(
                  "Translation field only accepts '|' or '-' characters."
                );
              }
            }}
            placeholder="Translation"
            className={`w-32 ${
              translationError ? "border border-red-500" : ""
            }`}
          />
          {translationError && (
            <span className="text-red-500 text-xs mt-1">
              {translationError}
            </span>
          )}
          <Button onClick={addFrequency} disabled={!isFrequencyValid}>
            Add
          </Button>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Translation</TableHead>
              <TableHead>Soft Deleted</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {frequencies.map((freq, idx) => (
              <TableRow key={idx} className={freq.soft_deleted ? "opacity-50" : ""}>
                <TableCell>{freq.name}</TableCell>
                <TableCell>{freq.description}</TableCell>
                <TableCell>{freq.translation}</TableCell>
                <TableCell>
                  <Checkbox
                    checked={!!freq.soft_deleted}
                    disabled={!!softDeleteLoading[freq.id]}
                    onCheckedChange={(checked) =>
                      toggleFrequencySoftDeleted(freq, checked === true)
                    }
                    title="Hide from patient page"
                  />
                </TableCell>
                <TableCell className="flex gap-2">
                  <Popover
                    open={!!editFreqPopover[freq.id]}
                    onOpenChange={(open) => {
                      setEditFreqPopover((prev) => ({
                        ...prev,
                        [freq.id]: open,
                      }));
                    }}
                  >
                    <PopoverTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        title="Edit"
                        onClick={() => startEditFrequency(freq)}
                      >
                        <UseAnimations animation={editIcon} size={24} />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-80">
                      <div className="font-semibold text-lg mb-2">
                        Edit Frequency
                      </div>
                      <label className="text-xs text-gray-500">Name</label>
                      <Input
                        value={editFreq.name}
                        onChange={(e) =>
                          setEditFreq((f) => ({ ...f, name: e.target.value }))
                        }
                        placeholder="Name"
                        className="mb-2"
                      />
                      <label className="text-xs text-gray-500">
                        Description
                      </label>
                      <Input
                        value={editFreq.description}
                        onChange={(e) =>
                          setEditFreq((f) => ({
                            ...f,
                            description: e.target.value,
                          }))
                        }
                        placeholder="Description"
                        className="mb-2"
                      />
                      <label className="text-xs text-gray-500">
                        Translation
                      </label>
                      <Input
                        value={editFreq.translation}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (/^[|\-]*$/.test(value)) {
                            setEditFreq((f) => ({ ...f, translation: value }));
                            setEditFreqTranslationError("");
                          } else {
                            setEditFreqTranslationError(
                              "Translation field only accepts '|' or '-' characters."
                            );
                          }
                        }}
                        placeholder="Translation"
                        className={`mb-2 ${
                          editFreqTranslationError ? "border border-red-500" : ""
                        }`}
                      />
                      {editFreqTranslationError && (
                        <span className="text-red-500 text-xs">
                          {editFreqTranslationError}
                        </span>
                      )}
                      <div className="flex gap-2 justify-end mt-2">
                        <Button
                          variant="outline"
                          onClick={() =>
                            setEditFreqPopover((prev) => ({
                              ...prev,
                              [freq.id]: false,
                            }))
                          }
                          disabled={editFreqLoading}
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={saveFrequencyEdit}
                          disabled={editFreqLoading}
                        >
                          Save
                        </Button>
                      </div>
                    </PopoverContent>
                  </Popover>
                  <Popover
                    open={!!deletePopover[freq.id]}
                    onOpenChange={(open) => {
                      setDeletePopover((prev) => ({
                        ...prev,
                        [freq.id]: open,
                      }));
                      if (!open) {
                        setDeleteInput((prev) => ({ ...prev, [freq.id]: "" }));
                      }
                    }}
                  >
                    <PopoverTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        title="Delete"
                        onClick={() =>
                          setDeletePopover((prev) => ({
                            ...prev,
                            [freq.id]: true,
                          }))
                        }
                      >
                        <UseAnimations animation={trash2} size={24} />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-72">
                      <div className="font-semibold text-lg mb-2">
                        Delete Frequency
                      </div>
                      <div className="mb-2">
                        Type <span className="font-bold">{freq.name}</span> to
                        confirm deletion.
                      </div>
                      <Input
                        value={deleteInput[freq.id] || ""}
                        onChange={(e) =>
                          setDeleteInput((prev) => ({
                            ...prev,
                            [freq.id]: e.target.value,
                          }))
                        }
                        placeholder="Type frequency name"
                        className="mb-2"
                        autoFocus
                      />
                      <div className="flex gap-2 justify-end">
                        <Button
                          variant="outline"
                          onClick={() => {
                            setDeletePopover((prev) => ({
                              ...prev,
                              [freq.id]: false,
                            }));
                            setDeleteInput((prev) => ({
                              ...prev,
                              [freq.id]: "",
                            }));
                          }}
                          disabled={deleteLoading}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="destructive"
                          disabled={
                            deleteInput[freq.id] !== freq.name || deleteLoading
                          }
                          onClick={async () => {
                            setDeleteLoading(true);
                            try {
                              await deleteFrequency(freq.id);
                              toast.success("Frequency deleted successfully");
                              setDeletePopover((prev) => ({
                                ...prev,
                                [freq.id]: false,
                              }));
                              setDeleteInput((prev) => ({
                                ...prev,
                                [freq.id]: "",
                              }));
                              fetchFrequencies();
                            } catch (err) {
                              toast.error("Failed to delete frequency");
                            } finally {
                              setDeleteLoading(false);
                            }
                          }}
                        >
                          Delete
                        </Button>
                      </div>
                    </PopoverContent>
                  </Popover>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <div>
        <h3 className="font-medium mb-2">Lengths</h3>
        <div className="flex gap-2 mb-2">
          <Input
            value={newLength.name}
            onChange={(e) =>
              setNewLength((l) => ({ ...l, name: e.target.value }))
            }
            placeholder="Name"
            className="w-32"
          />
          <Input
            value={newLength.description}
            onChange={(e) =>
              setNewLength((l) => ({ ...l, description: e.target.value }))
            }
            placeholder="Description"
            className="w-48"
          />
          <Button onClick={addLength} disabled={!isLengthValid}>
            Add
          </Button>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Soft Deleted</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {lengths.map((len, idx) => (
              <TableRow key={idx} className={len.soft_deleted ? "opacity-50" : ""}>
                <TableCell>{len.name}</TableCell>
                <TableCell>{len.description}</TableCell>
                <TableCell>
                  <Checkbox
                    checked={!!len.soft_deleted}
                    disabled={!!softDeleteLoading[len.id]}
                    onCheckedChange={(checked) =>
                      toggleLengthSoftDeleted(len, checked === true)
                    }
                    title="Hide from patient page"
                  />
                </TableCell>
                <TableCell className="flex gap-2">
                  <Popover
                    open={!!editLengthPopover[len.id]}
                    onOpenChange={(open) => {
                      setEditLengthPopover((prev) => ({
                        ...prev,
                        [len.id]: open,
                      }));
                    }}
                  >
                    <PopoverTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        title="Edit"
                        onClick={() => startEditLength(len)}
                      >
                        <UseAnimations animation={editIcon} size={24} />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-80">
                      <div className="font-semibold text-lg mb-2">
                        Edit Length
                      </div>
                      <label className="text-xs text-gray-500">Name</label>
                      <Input
                        value={editLengthState.name}
                        onChange={(e) =>
                          setEditLengthState((l) => ({
                            ...l,
                            name: e.target.value,
                          }))
                        }
                        placeholder="Name"
                        className="mb-2"
                      />
                      <label className="text-xs text-gray-500">
                        Description
                      </label>
                      <Input
                        value={editLengthState.description}
                        onChange={(e) =>
                          setEditLengthState((l) => ({
                            ...l,
                            description: e.target.value,
                          }))
                        }
                        placeholder="Description"
                        className="mb-2"
                      />
                      <div className="flex gap-2 justify-end mt-2">
                        <Button
                          variant="outline"
                          onClick={() =>
                            setEditLengthPopover((prev) => ({
                              ...prev,
                              [len.id]: false,
                            }))
                          }
                          disabled={editLengthLoading}
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={saveLengthEdit}
                          disabled={editLengthLoading}
                        >
                          Save
                        </Button>
                      </div>
                    </PopoverContent>
                  </Popover>
                  <Popover
                    open={!!lengthDeletePopover[len.name]}
                    onOpenChange={(open) => {
                      setLengthDeletePopover((prev) => ({
                        ...prev,
                        [len.name]: open,
                      }));
                      if (!open) {
                        setLengthDeleteInput((prev) => ({
                          ...prev,
                          [len.name]: "",
                        }));
                      }
                    }}
                  >
                    <PopoverTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        title="Delete"
                        onClick={() =>
                          setLengthDeletePopover((prev) => ({
                            ...prev,
                            [len.name]: true,
                          }))
                        }
                      >
                        <UseAnimations animation={trash2} size={24} />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-72">
                      <div className="font-semibold text-lg mb-2">
                        Delete Length
                      </div>
                      <div className="mb-2">
                        Type <span className="font-bold">{len.name}</span> to
                        confirm deletion.
                      </div>
                      <Input
                        value={lengthDeleteInput[len.name] || ""}
                        onChange={(e) =>
                          setLengthDeleteInput((prev) => ({
                            ...prev,
                            [len.name]: e.target.value,
                          }))
                        }
                        placeholder="Type length name"
                        className="mb-2"
                        autoFocus
                      />
                      <div className="flex gap-2 justify-end">
                        <Button
                          variant="outline"
                          onClick={() => {
                            setLengthDeletePopover((prev) => ({
                              ...prev,
                              [len.name]: false,
                            }));
                            setLengthDeleteInput((prev) => ({
                              ...prev,
                              [len.name]: "",
                            }));
                          }}
                          disabled={lengthDeleteLoading}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="destructive"
                          disabled={
                            lengthDeleteInput[len.name] !== len.name ||
                            lengthDeleteLoading
                          }
                          onClick={async () => {
                            setLengthDeleteLoading(true);
                            try {
                              await deleteLength(len.id);
                              toast.success("Length deleted successfully");
                              setLengthDeletePopover((prev) => ({
                                ...prev,
                                [len.name]: false,
                              }));
                              setLengthDeleteInput((prev) => ({
                                ...prev,
                                [len.name]: "",
                              }));
                              fetchLengths();
                            } catch (err) {
                              toast.error("Failed to delete length");
                            } finally {
                              setLengthDeleteLoading(false);
                            }
                          }}
                        >
                          Delete
                        </Button>
                      </div>
                    </PopoverContent>
                  </Popover>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default PrescriptionFrequencyLength;
